import React, { useCallback, useEffect, useState } from 'react';
import {
  RightOutlined,
  FileTextOutlined,
  WarningOutlined,
  CloseOutlined,
  LoadingOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';
import {
  getJobInfoForPrint,
  sectionFetch,
  getJobAttachments
} from '../../../Apis/Jobs';
import { fetchJobAuditDataByJobId } from '../../../Apis/Facility';
import { getDataOfPatient } from '../../../Apis/Patient';
import AuditTimeline from './AuditTimeline';
import FaceSheetPdf from './FaceSheetPdf';
import ReportPdf from './ReportPdf';
import NoteEmrPdf from './NoteEmrPdf';
import {
  DetailColumn,
  DetailBar,
  DetailTitleWrap,
  DetailTitle,
  Acts,
  StatusBadge,
  HeaderCard,
  FactGrid,
  FactKey,
  FactVal,
  BodyGrid,
  Tabs,
  Tab,
  Panel,
  EmptyState,
  NoteSection,
  NoteSectionHead,
  NoteSectionTitle,
  SectionTag,
  NoteSectionBody,
  SuppressLine,
  AiCard,
  AiCardHead,
  AiCardBody,
  FieldLabel,
  CodeBlock,
  Kv,
  Attachment,
  DetailCloseButton,
  SkeletonBlock
} from './AuditDetailStyles';

const Fact = ({ label, value }) => (
  <div>
    <FactKey>{label}</FactKey>
    <FactVal>{value || '—'}</FactVal>
  </div>
);

/**
 * Redesigned audit detail panel (Jobs tab) — header card, tabbed body
 * (Timeline / Note Information / AI Activity / Attachments) and a right rail.
 * All data is fetched per selected job; no new endpoints are used.
 */
const AuditDetailPanel = ({ session, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState(null);
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [aiGenerations, setAiGenerations] = useState([]);
  const [sections, setSections] = useState([]);
  const [notePdfLink, setNotePdfLink] = useState(null);
  const [activeTab, setActiveTab] = useState('att');
  const [openSections, setOpenSections] = useState({});
  const [openAi, setOpenAi] = useState({});
  // { jobId, win } while a face-sheet PDF is being generated for the new tab.
  const [pdfJob, setPdfJob] = useState(null);
  // { printOption, win } while an Audit / Note-with-Audit report PDF is generating.
  const [reportJob, setReportJob] = useState(null);
  // { win } while the EMR Note PDF is being generated (no backend file link).
  const [noteJob, setNoteJob] = useState(null);

  const jobId = session?.jobId;

  // Open the tab synchronously (inside the click) so popup blockers allow it,
  // seeded with a loading spinner; the generated PDF replaces it when ready.
  const openLoadingWindow = () => {
    const win = window.open('', '_blank');
    if (win) {
      win.document.open();
      win.document.write(`
        <!doctype html><html><head><title>Generating PDF</title>
        <style>
          html,body{margin:0;height:100%}
          body{display:flex;flex-direction:column;align-items:center;justify-content:center;
            font-family:'Krub',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
            background:#F0F2F5;color:#525E6F}
          .spinner{width:44px;height:44px;border:4px solid #E8ECF0;border-top-color:#47A4EA;
            border-radius:50%;animation:spin .8s linear infinite}
          @keyframes spin{to{transform:rotate(360deg)}}
          p{margin-top:16px;font-size:14px;font-weight:500}
        </style></head>
        <body><div class="spinner"></div><p>Generating PDF…</p></body></html>
      `);
      win.document.close();
    }
    return win;
  };

  const openFaceSheetPdf = () => {
    if (!jobId) return;
    setPdfJob({ jobId, win: openLoadingWindow() });
  };

  // Generate the "Patient Report" (Audit/SessionDetails) as a PDF for the given
  // print option ('audit' | 'combined'), matching the notes-board sidebar.
  const openReportPdf = printOption => {
    if (!jobId) return;
    setReportJob({ printOption, win: openLoadingWindow() });
  };

  // Client-side EMR Note PDF (emr-note-pdf design) when no backend file exists.
  const openNotePdf = () => {
    if (!jobId) return;
    setNoteJob({ win: openLoadingWindow() });
  };

  const loadDetail = useCallback(() => {
    if (!jobId) return;
    setLoading(true);
    setHeader(null);
    setPatient(null);
    setTimeline([]);
    setAiGenerations([]);
    setSections([]);
    setNotePdfLink(null);
    setOpenSections({});
    setOpenAi({});
    setActiveTab('att');

    const timelinePromise = fetchJobAuditDataByJobId(jobId)
      .then(response => {
        if (response.data.success) {
          setTimeline(response.data.result || []);
          setAiGenerations(response.data.aiJobSectionGenerations || []);
        }
      })
      .catch(err => console.log(err));

    const headerPromise = getJobInfoForPrint(jobId)
      .then(response => {
        if (response.data.success === true) {
          const info = response.data.result?.[0] || null;
          setHeader(info);
          if (info?.facility?.facilityId && info?.patientId) {
            return getDataOfPatient(info.facility.facilityId, info.patientId)
              .then(res => {
                if (res.data.success) setPatient(res.data.result?.[0] || null);
              })
              .catch(err => console.log(err));
          }
        }
      })
      .catch(err => console.log(err));

    const sectionPromise = sectionFetch(jobId)
      .then(response => {
        if (response.data.success) {
          setSections(response.data.result?.[0]?.sections || []);
        }
      })
      .catch(err => console.log(err));

    const attachmentsPromise = getJobAttachments(jobId)
      .then(response => {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.result || [];
        const link = list.find(a => a?.fileLink)?.fileLink || null;
        setNotePdfLink(link);
      })
      .catch(err => console.log(err));

    Promise.allSettled([
      timelinePromise,
      headerPromise,
      sectionPromise,
      attachmentsPromise
    ]).then(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const toggleSection = idx =>
    setOpenSections(prev => ({ ...prev, [idx]: !prev[idx] }));

  const toggleAi = key => setOpenAi(prev => ({ ...prev, [key]: !prev[key] }));

  const noteType =
    header?.dictationTemplate?.templateName || header?.workType || '';
  const cptCodes = session?.cptCodesInContent;
  const cptCodeDisplay = cptCodes?.length
    ? cptCodes
        .map(c => c.cptCode)
        .filter(Boolean)
        .join(', ')
    : null;
  const includedCount = sections.filter(
    s => s.heading?.submitToEmr !== false
  ).length;
  const aiPromptCount = aiGenerations.length + (session?.mips ? 1 : 0);

  // The AI Activity tab is hidden for billers.
  let currentUserType = null;
  try {
    currentUserType =
      JSON.parse(localStorage.getItem('user'))?.userType ?? null;
  } catch (e) {
    currentUserType = null;
  }
  const showAiTab = currentUserType !== 'biller';

  const tabs = [
    { key: 'att', label: 'Attachments', count: 1 },
    { key: 'note', label: 'Note Information', count: includedCount },
    ...(showAiTab
      ? [{ key: 'ai', label: 'AI Activity', count: aiPromptCount }]
      : []),
    { key: 'timeline', label: 'Timeline', count: timeline.length }
  ];

  if (!session) {
    return (
      <DetailColumn>
        <EmptyState style={{ paddingTop: 80 }}>
          Select a job from the list to view its audit details.
        </EmptyState>
      </DetailColumn>
    );
  }

  return (
    <DetailColumn>
      {pdfJob && (
        <FaceSheetPdf
          jobId={pdfJob.jobId}
          targetWindow={pdfJob.win}
          onComplete={() => setPdfJob(null)}
        />
      )}
      {reportJob && (
        <ReportPdf
          session={session}
          printOption={reportJob.printOption}
          header={header}
          patient={patient}
          aiGenerations={aiGenerations}
          targetWindow={reportJob.win}
          onComplete={() => setReportJob(null)}
        />
      )}
      {noteJob && (
        <NoteEmrPdf
          header={header}
          patient={patient}
          sections={sections}
          session={session}
          targetWindow={noteJob.win}
          onComplete={() => setNoteJob(null)}
        />
      )}
      <DetailBar>
        <DetailTitleWrap>
          <DetailTitle>Audit Details</DetailTitle>
          <StatusBadge status={session?.status}>
            {session?.status || '—'}
          </StatusBadge>
        </DetailTitleWrap>
        <Acts>
          <DetailCloseButton
            type='button'
            aria-label='Close audit details'
            onClick={onClose}
          >
            <CloseOutlined />
          </DetailCloseButton>
        </Acts>
      </DetailBar>

      {loading ? (
        <>
          <HeaderCard style={{ padding: '28px 24px' }}>
            <FactGrid style={{ rowGap: 28 }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i}>
                  <SkeletonBlock
                    height='12px'
                    width='55%'
                    style={{ marginBottom: 12 }}
                  />
                  <SkeletonBlock height='20px' width='88%' />
                </div>
              ))}
            </FactGrid>
          </HeaderCard>
          <Panel>
            <SkeletonBlock
              height='16px'
              width='150px'
              style={{ marginBottom: 18 }}
            />
            {['96%', '88%', '92%', '70%', '84%'].map((w, i) => (
              <SkeletonBlock
                key={i}
                height='12px'
                width={w}
                style={{ marginBottom: 12 }}
              />
            ))}
          </Panel>
        </>
      ) : (
        <>
          <HeaderCard>
            <FactGrid>
              <Fact
                label='Patient'
                value={header?.patientName || session?.patientName}
              />
              <Fact label='Author' value={header?.author || session?.author} />
              <Fact
                label='Job Number'
                value={session?.jobNumber || header?.jobNumber}
              />
              <Fact
                label='Medical Record Number'
                value={patient?.medicalRecordNumber}
              />
              <Fact
                label='Encounter Date'
                value={
                  header?.effectiveDate
                    ? formatDateTime(header.effectiveDate)
                    : session?.effectiveDate
                    ? formatDateTime(session.effectiveDate)
                    : ''
                }
              />
              <Fact
                label='Admission Date'
                value={
                  patient?.admissionDate
                    ? formatDate(patient.admissionDate)
                    : ''
                }
              />
              <Fact
                label='Facility'
                value={
                  header?.facility?.facilityName ||
                  session?.facility?.facilityName
                }
              />
              <Fact label='Note Type' value={noteType} />
              <Fact label='Room/Bed ID' value={patient?.bedId} />
              <Fact label='Job ID' value={jobId} />
              <Fact label='CPT Code' value={cptCodeDisplay} />
            </FactGrid>
          </HeaderCard>

          <BodyGrid>
            <div>
              <Tabs role='tablist'>
                {tabs.map(t => (
                  <Tab
                    key={t.key}
                    active={activeTab === t.key}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </Tab>
                ))}
              </Tabs>

              {activeTab === 'timeline' && (
                <Panel>
                  <h3>Timeline</h3>
                  <p className='sub'>Every action taken on this note.</p>
                  <AuditTimeline events={timeline} />
                </Panel>
              )}

              {activeTab === 'note' && (
                <Panel>
                  <h3>Submitted note</h3>
                  <p className='sub'>
                    {includedCount} section{includedCount === 1 ? '' : 's'} sent
                    to the EHR
                    {sections.length - includedCount > 0
                      ? ` · ${sections.length - includedCount} suppressed`
                      : ''}
                    .
                  </p>
                  {cptCodes?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <FactKey style={{ marginBottom: 8 }}>CPT Codes</FactKey>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6
                        }}
                      >
                        {cptCodes.map((c, i) => (
                          <div
                            key={i}
                            style={{
                              background: '#F5F7FA',
                              borderRadius: 6,
                              padding: '8px 12px'
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: '#1C2B3A'
                              }}
                            >
                              {c.cptCode}
                            </div>
                            {c.description && (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: '#525E6F',
                                  marginTop: 2
                                }}
                              >
                                {c.description}
                              </div>
                            )}
                            {c.category && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#8A96A3',
                                  marginTop: 2
                                }}
                              >
                                {c.category}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {sections.length ? (
                    sections.map((section, idx) => {
                      const suppressed = section.heading?.submitToEmr === false;
                      const open = openSections[idx] ?? !suppressed;
                      return (
                        <NoteSection key={section.id || idx}>
                          <NoteSectionHead
                            open={open}
                            onClick={() => toggleSection(idx)}
                          >
                            <RightOutlined
                              className='chev'
                              style={{ fontSize: 11 }}
                            />
                            <NoteSectionTitle>
                              {section.heading?.customUIHeading || 'Section'}
                            </NoteSectionTitle>
                            <SectionTag suppressed={suppressed}>
                              {suppressed ? 'Suppressed' : 'Included'}
                            </SectionTag>
                          </NoteSectionHead>
                          {open && (
                            <NoteSectionBody>
                              {suppressed && (
                                <SuppressLine>
                                  <WarningOutlined style={{ marginTop: 1 }} />
                                  Not submitted to the EHR
                                  {section.heading?.suppressOnEmpty
                                    ? ' (suppress-on-empty)'
                                    : ''}
                                  . Retained for audit.
                                </SuppressLine>
                              )}
                              {(section.content || []).map((item, k) => (
                                <div
                                  key={k}
                                  className='ql-editor'
                                  dangerouslySetInnerHTML={{
                                    __html: item.value || ''
                                  }}
                                />
                              ))}
                              {!(section.content || []).length && (
                                <p style={{ color: '#A0AAB4' }}>
                                  <em>No content.</em>
                                </p>
                              )}
                            </NoteSectionBody>
                          )}
                        </NoteSection>
                      );
                    })
                  ) : (
                    <EmptyState>No sections found.</EmptyState>
                  )}
                </Panel>
              )}

              {activeTab === 'ai' && (
                <Panel>
                  <h3>AI provenance</h3>
                  <p className='sub'>
                    Exact prompt and raw model output. Retained for compliance.
                  </p>
                  {aiGenerations.length || session?.mips ? (
                    <>
                      {aiGenerations.map((run, idx) => {
                        const key = `ai-${idx}`;
                        const open = !!openAi[key];
                        return (
                          <AiCard key={key}>
                            <AiCardHead
                              open={open}
                              onClick={() => toggleAi(key)}
                            >
                              <div className='ic'>
                                <ThunderboltOutlined />
                              </div>
                              <div className='info'>
                                <div className='nm'>
                                  {run.fullName || 'AI run'}
                                </div>
                                <div className='mt'>
                                  {formatDateTime(run.time)}
                                </div>
                              </div>
                              <RightOutlined
                                className='chev'
                                style={{ fontSize: 11 }}
                              />
                            </AiCardHead>
                            {open && (
                              <AiCardBody>
                                <FieldLabel>Prompt request</FieldLabel>
                                <CodeBlock>
                                  {run.request?.find(r => r.role === 'user')
                                    ?.content || 'No prompt stored.'}{' '}
                                </CodeBlock>
                                <FieldLabel>Raw response</FieldLabel>
                                <CodeBlock scroll>
                                  {run.response || 'No response stored.'}
                                </CodeBlock>
                              </AiCardBody>
                            )}
                          </AiCard>
                        );
                      })}
                      {session?.mips && (
                        <AiCard>
                          <AiCardHead
                            open={!!openAi.mips}
                            onClick={() => toggleAi('mips')}
                          >
                            <div className='ic'>
                              <ThunderboltOutlined />
                            </div>
                            <div className='info'>
                              <div className='nm'>
                                MIPS Score Generation
                                {session.mips.fullName
                                  ? ` · ${session.mips.fullName}`
                                  : ''}
                              </div>
                              <div className='mt'>
                                {formatDateTime(session.mips.timestamp)}
                              </div>
                            </div>
                            <RightOutlined
                              className='chev'
                              style={{ fontSize: 11 }}
                            />
                          </AiCardHead>
                          {openAi.mips && (
                            <AiCardBody>
                              <Kv>
                                <span className='k'>Score</span>
                                <span className='v'>
                                  {session.mips.score}/100
                                </span>
                              </Kv>
                              <FieldLabel>Prompt</FieldLabel>
                              <CodeBlock>
                                {session.mips.prompt || 'No prompt stored.'}
                              </CodeBlock>
                              <FieldLabel>Suggestions</FieldLabel>
                              <CodeBlock scroll>
                                {session.mips.suggestions || 'No suggestions.'}
                              </CodeBlock>
                            </AiCardBody>
                          )}
                        </AiCard>
                      )}
                    </>
                  ) : (
                    <EmptyState>No AI runs recorded for this note.</EmptyState>
                  )}
                </Panel>
              )}

              {activeTab === 'att' && (
                <Panel>
                  <h3>Attachments</h3>
                  <p className='sub'>Generated documents for this note.</p>
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                  >
                    {(currentUserType === 'biller'
                      ? ['Note PDF', 'Face sheet']
                      : ['Note PDF', 'Audit', 'Note with audit', 'Face sheet']
                    ).map(name => {
                      const isNotePdf = name === 'Note PDF';
                      const isFaceSheet = name === 'Face sheet';
                      const reportOption =
                        name === 'Audit'
                          ? 'audit'
                          : name === 'Note with audit'
                          ? 'combined'
                          : null;

                      const busy = !!pdfJob || !!reportJob || !!noteJob;
                      const generating =
                        (isFaceSheet && !!pdfJob) ||
                        (isNotePdf && !!noteJob) ||
                        (!!reportOption &&
                          reportJob?.printOption === reportOption);

                      let clickable = false;
                      let onClick;
                      if (isNotePdf) {
                        // Open the backend file link if present; otherwise
                        // generate the EMR note PDF client-side.
                        if (notePdfLink) {
                          clickable = true;
                          onClick = () =>
                            window.open(notePdfLink, '_blank', 'noopener');
                        } else {
                          clickable = !!jobId && !busy;
                          onClick = clickable ? openNotePdf : undefined;
                        }
                      } else if (isFaceSheet) {
                        clickable = !!jobId && !busy;
                        onClick = clickable ? openFaceSheetPdf : undefined;
                      } else if (reportOption) {
                        clickable = !!jobId && !busy;
                        onClick = clickable
                          ? () => openReportPdf(reportOption)
                          : undefined;
                      }

                      return (
                        <Attachment
                          key={name}
                          style={{
                            cursor: generating
                              ? 'progress'
                              : clickable
                              ? 'pointer'
                              : 'default'
                          }}
                          onClick={onClick}
                        >
                          <div className='ic'>
                            {generating ? (
                              <LoadingOutlined spin />
                            ) : (
                              <FileTextOutlined />
                            )}
                          </div>
                          <div>
                            <div className='nm'>{name}</div>
                            {generating && (
                              <div className='mt'>Generating PDF…</div>
                            )}
                          </div>
                        </Attachment>
                      );
                    })}
                  </div>
                </Panel>
              )}
            </div>
          </BodyGrid>
        </>
      )}
    </DetailColumn>
  );
};

export default AuditDetailPanel;
